import { useEffect, useRef } from 'react';
import { getSocket, connectSocket, disconnectSocket } from '@/services/socket';
import { useAuthStore } from '@/stores/authStore';

// ============================================================
// Hook per gestione connessione e ascolto eventi Socket.IO
// ============================================================

/**
 * Hook che gestisce la connessione/disconnessione del socket
 * in base allo stato di autenticazione dell'utente.
 * Da utilizzare una sola volta nel layout principale.
 */
export function useSocketConnection() {
  const tokens = useAuthStore((s) => s.tokens);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated && tokens?.accessToken) {
      connectSocket(tokens.accessToken);
    } else {
      disconnectSocket();
    }
    return () => {
      disconnectSocket();
    };
  }, [isAuthenticated, tokens?.accessToken]);
}

/**
 * Hook per ascoltare un evento specifico dal socket.
 * Si registra automaticamente e pulisce il listener on unmount.
 *
 * @example
 * useSocketEvent<Notification>('notification:new', (data) => {
 *   console.log('Nuova notifica:', data);
 * });
 */
export function useSocketEvent<T = unknown>(event: string, handler: (data: T) => void) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const listener = (data: T) => handlerRef.current(data);
    socket.on(event, listener);
    return () => {
      socket.off(event, listener);
    };
  }, [event]);
}
